import { useEffect, useState } from 'react';
import { captureSummaryV3, fmtCount, fmtMoneyCompact, residentsNeedingAction, type PortfolioBoard, type ResidentsNeedingAction } from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { sx } from '../../ui/designStyles';
import { exportCsv } from '../../ui/exportCsv';
import { saveBlob, textPdf } from '../../ui/textPdf';
import { usePerf, type PerfCtx } from './ctx';
import { COLS, GRADE_STYLE, gradeOf, heatA, ord, pc, pre, usePerfApi } from './lib';
import { BenchFrame, benchRows, cellsOf, setBenchUi, srcOf, useBenchUi, type BenchRow } from './portfolioHead';
import { BuildTagged, useBuildText, useCustomerText } from '../../ui/buildLabels';
import { PortfolioRollup, SideBySide, compareChip } from './PortfolioRollup';

const GRID = 'display:grid;grid-template-columns:24px 200px 38px 48px 40px 68px 60px 62px repeat(16,1fr);gap:5px;';
const HEAD = 'font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:600;';
const KPI_CELL = 'border-top:1px solid rgba(34,39,46,.14);padding:7px 14px 8px;display:grid;grid-template-columns:auto minmax(0,1fr);align-items:baseline;gap:0 10px;';
const KPI_K = 'font-variant-numeric:tabular-nums;font-size:14px;letter-spacing:.14em;text-transform:uppercase;color:#54606C;font-weight:600;grid-column:1 / -1;';
const KPI_V = 'font-size:22px;font-weight:800;letter-spacing:-.03em;margin-top:2px;line-height:1.1;';
const SECTION = 'font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;';
const STRIP_COL = 'display:flex;flex-direction:column;gap:6px;min-width:0;padding:0 16px;border-left:1px solid rgba(34,39,46,.1);';
const TAG = 'display:inline-flex;align-items:center;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.08em;border-radius:4px;padding:2px 7px;';
const MIX_C: Record<string, string> = { A: '#1F6B52', B: '#2F5C97', C: '#5B6B7C', D: '#B3261E', F: '#B3261E' };

/** Points to the next grade band, or null in the top band (design nextGrade, L34070). */
function nextGrade(c: number): { pts: number; grade: string } | null {
  if (c >= 80) return null;
  const up: [number, string] = c >= 72 ? [80, 'A'] : c >= 62 ? [72, 'B'] : c >= 52 ? [62, 'C'] : [52, 'D'];
  return { pts: up[0] - c, grade: up[1] };
}
const usdK = (usd: number | null) => (usd == null ? '–' : '$' + Math.round(usd / 1000) + 'K');
const median = (xs: number[]) => {
  const s = xs.slice().sort((a, b) => a - b);
  return s.length ? (s.length % 2 ? s[(s.length - 1) / 2] : Math.round((s[s.length / 2 - 1] + s[s.length / 2]) / 2)) : 0;
};
const pts = (n: number) => n + (Math.abs(n) === 1 ? ' point' : ' points');
const dagger = (ci: number) => (ci >= 7 && ci <= 12 ? ' †' : '');
/** The long dash the server's reasons use, which the console never prints. */
const LONG_DASH = new RegExp('\\s*' + String.fromCharCode(8212) + '\\s*', 'g');
/** The server's reason as the design writes it: lower case, no full stop, a comma for the dash. */
const reasonText = (r: string | null) => (r ? (r.charAt(0).toLowerCase() + r.slice(1)).replace(LONG_DASH, ', ').replace(/\.$/, '') : 'not scoreable');

/**
 * Portfolio (design bPortfolio, T16471-16503 and T16627-16832): Operator, Super Admin and
 * Partner. Every building the operator manages, graded against its peers, as a heatmap of
 * percentiles; a row opens its peer bands and the residents needing action (masked).
 */
export function PortfolioTab() {
  const plain = useCustomerText();
  const ctx = usePerf();
  const u = useBenchUi();
  const { client } = useSession();
  // The server refuses the Partner a building's residents (QA-12): the row still opens its
  // peer bands, and says who sees the residents instead of asking for them.
  const partner = useConsoleRole().role === 'PARTNER';
  const board = ctx.board?.data as PortfolioBoard | null | undefined;
  const { shown, unscored } = benchRows(ctx, u);
  // The network figures follow the facility switcher's pick, as the rows do.
  const network = usePerfApi((c, signal) => captureSummaryV3(c, { query: ctx.facilities ? { facilities: ctx.facilities } : undefined, signal }), [ctx.facilities]);
  const bt = useBuildText();
  const [res, setRes] = useState<Record<string, ResidentsNeedingAction | 'none'>>({});
  // V9 Compare: two buildings side by side (design pfCmp); a third pick drops the oldest.
  const [cmp, setCmp] = useState<string[]>([]);
  const toggleCmp = (id: string) => setCmp((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id].slice(-2)));
  const entryOf = (id: string) => board?.facilities.find((f) => f.id === id);

  const toggleRow = (r: BenchRow) => {
    setBenchUi({ openRow: u.openRow === r.id ? null : r.id });
  };
  // The open row lives in the shared bench state and outlives this tab (a resize that
  // remounts it keeps the row open), so its residents load whenever it is open and missing.
  const openId = u.openRow;
  const have = openId ? res[openId] !== undefined : true;
  useEffect(() => {
    if (!openId || have || partner) return;
    residentsNeedingAction(client, { params: { id: openId } })
      .then((x) => setRes((m) => ({ ...m, [openId]: x })))
      .catch(() => setRes((m) => ({ ...m, [openId]: 'none' })));
  }, [client, openId, have, partner]);

  const comps = shown.map((r) => r.comp);
  const med = median(comps);
  const best = shown.length ? shown.slice().sort((a, b) => b.comp - a.comp)[0] : null;
  const worst = shown.length ? shown.slice().sort((a, b) => a.comp - b.comp)[0] : null;
  // The design reads best and worst off the sorted list; with the default sort they are its ends.
  const first = u.sortBy === 'comp' ? shown[0] ?? null : best;
  const last = u.sortBy === 'comp' ? shown[shown.length - 1] ?? null : worst;
  const counts: Record<string, number> = { A: 0, B: 0, C: 0, D: 0, F: 0 };
  shown.forEach((r) => {
    const g = gradeOf(r.comp);
    counts[g] += 1;
  });
  const gradeMix = ['A', 'B', 'C', 'D', 'F'].filter((g) => counts[g] > 0).map((g) => ({ label: g + ' ' + counts[g], color: MIX_C[g], w: ((counts[g] / (shown.length || 1)) * 100).toFixed(1) + '%' }));
  const peerLabel = ctx.peerLabel;
  const peerName = ctx.peer?.name ?? 'national';
  const peerN = ctx.peer?.n ?? ctx.fac?.peerN ?? 0;
  const peerMed = ctx.peer?.median ?? null;

  // Priorities: the building on screen, only metrics below the peer median, lowest first (design bPrioRows).
  const facCells = cellsOf(ctx.fac?.metrics);
  const scored = facCells.filter((c) => c !== null).length;
  const prio = facCells
    .map((p, i) => ({ i, p }))
    .filter((x): x is { i: number; p: number } => x.p !== null && x.p < 50)
    .sort((a, b) => a.p - b.p)
    .slice(0, 3)
    .map((x) => {
      const gain = Math.max(1, Math.round((50 - x.p) / (scored || 1)));
      return { label: COLS[x.i].label, pct: 'p' + x.p, w: Math.max(3, x.p) + '%', color: x.p < 25 ? '#B3261E' : '#5B6B7C', gain: 'Reaching the typical peer figure would move the composite by about +' + gain + (gain === 1 ? ' point.' : ' points.') };
    });
  const prioNote = prio.length
    ? 'Ranked by percentile against ' + peerLabel + ', lowest first, and only metrics below typical appear. The point gain assumes every other metric holds.'
    : 'Every scored metric for this facility is at or above the typical figure of ' + peerLabel + '. Nothing here is a priority; look at the portfolio instead.';

  const V = (col: string) => 'flex:0 0 auto;font-size:14px;font-weight:800;font-variant-numeric:tabular-nums;white-space:nowrap;color:' + col + ';';
  const near = shown
    .map((r) => ({ r, nx: nextGrade(r.comp) }))
    .filter((x): x is { r: BenchRow; nx: { pts: number; grade: string } } => !!x.nx)
    .sort((a, b) => a.nx.pts - b.nx.pts)
    .slice(0, 3)
    .map((x) => ({ key: x.r.id, a: x.r.name, b: '+' + x.nx.pts + ' → ' + x.nx.grade, v: usdK(x.r.upsideUsd), c: '#1F6B52', id: x.r.id, tip: x.r.name + ' needs ' + x.nx.pts + ' composite points for ' + x.nx.grade }));
  const weak = COLS.map((c, ci) => {
    const vals = shown.map((r) => ({ r, c: r.cells[ci] })).filter((x): x is { r: BenchRow; c: number } => x.c !== null);
    if (vals.length < 3) return null;
    const sorted = vals.map((x) => x.c).sort((a, b) => a - b);
    return { label: c.label, med: sorted[Math.floor(sorted.length / 2)], drag: vals.slice().sort((a, b) => a.c - b.c)[0] };
  })
    .filter((x): x is NonNullable<typeof x> => !!x)
    .sort((a, b) => a.med - b.med)
    .slice(0, 3)
    .map((w) => ({ key: w.label, a: w.label, b: 'drag: ' + w.drag.r.name, v: 'p50 ' + ord(w.med), c: w.med < 40 ? '#B3261E' : '#5B6B7C', id: w.drag.r.id, tip: w.label + ': typical across the portfolio ' + ord(w.med) + '. Lowest: ' + w.drag.r.name + ' at ' + ord(w.drag.c) }));
  const proj = shown
    .filter((r) => r.delta !== null)
    .map((r) => {
      const q = r.comp + Math.round((r.delta as number) / 3);
      return { r, q, g0: gradeOf(r.comp), g1: gradeOf(q) };
    })
    .filter((x) => x.g0 !== x.g1)
    .slice(0, 3)
    .map((x) => ({ key: x.r.id, a: x.r.name, b: x.g0 + ' → ' + x.g1, v: String(x.q), c: x.q > x.r.comp ? '#1F6B52' : '#B3261E', id: x.r.id, tip: 'Last quarter change carried 30 days forward. An estimate, not a promise.' }));
  const pmCols = [
    { head: 'Closest to the next grade', tag: '', rows: near, empty: 'Every building is in the top band.' },
    { head: 'Weakest metrics across the portfolio', tag: '', rows: weak, empty: 'Needs at least 3 buildings in view.' },
    { head: 'Grade changes in 30 days', tag: 'MODELED EST.', rows: proj, empty: shown.some((r) => r.delta !== null) ? 'No building is projected to change grade.' : plain('Needs each building’s quarter-on-quarter change, which is not served yet.') },
  ];

  const leakRows = shown.filter((r) => r.leakYr !== null);
  const outliers = [
    ...(best ? [{ name: best.name, tag: 'BEST IN GROUP', why: 'Composite ' + best.comp + ', ' + pts(best.comp - med) + ' above the portfolio’s typical of ' + med + ' and the highest of the ' + shown.length + ' graded. Worth reading its staffing pattern before the next budget round.', tagStyle: TAG + 'color:#1F6B52;background:rgba(47,138,111,.13);' }] : []),
    ...(worst
      ? [{
          name: worst.name,
          tag: 'WEAKEST',
          why:
            'Composite ' + worst.comp + ', ' + pts(med - worst.comp) + ' below the portfolio’s typical of ' + med + '. That is the largest single composite gap among the facilities you manage' +
            (peerMed == null ? '.' : ', and against the ' + peerName + ' typical of ' + peerMed + ' it sits ' + (worst.comp === peerMed ? 'level.' : pts(Math.abs(worst.comp - peerMed)) + ' ' + (worst.comp > peerMed ? 'above.' : 'below.'))),
          tagStyle: TAG + 'color:#B3261E;background:rgba(179,38,30,.13);',
        }]
      : []),
    ...(leakRows.length
      ? leakRows
          .slice()
          .sort((a, b) => (b.leakYr as number) - (a.leakYr as number))
          .slice(0, 1)
          .map((f) => ({ name: f.name, tag: 'LEAKAGE', why: 'Largest uncaptured reimbursement in the portfolio at $' + (f.leakYr as number).toLocaleString('en-US') + 'K/yr, independent of its clinical grade.', tagStyle: TAG + 'color:#5B6B7C;background:rgba(91,107,124,.14);' }))
      : []),
  ];
  const market = [
    { k: 'Facilities in the ' + peerName + ' group', v: peerN.toLocaleString('en-US') },
    { k: 'Median composite, ' + peerName + ' group', v: peerMed == null ? '–' : String(peerMed) },
    { k: 'Median composite, your portfolio', v: String(med) },
    { k: 'Beds under management', v: shown.reduce((a, f) => a + f.beds, 0).toLocaleString('en-US') },
    { k: 'Portfolio leakage', v: leakRows.length ? '$' + leakRows.reduce((a, f) => a + (f.leakYr as number), 0).toLocaleString('en-US') + 'K/yr' : '–' },
  ];
  const marketNote = 'Two typical figures, deliberately: the peer group is every comparable facility in the ' + peerName + ' set, your portfolio is the ' + shown.length + ' graded here. Outliers are measured against the portfolio’s typical figure. Change the tier and the group figures move.';

  // The Overview's capture line, from the network capture summary (design ovAgingLine).
  const n = network.data;
  const agingLine =
    n && n.engine && n.denialRate
      ? bt('Engine ' + (n.engine.live ? 'live' : 'not live') + ' · ' + fmtCount(n.engine.connectedFacilities) + ' ' + (n.engine.connectedFacilities === 1 ? 'facility' : 'facilities') + ' connected · dollars, windows and denials ' +
        (n.recoverable.basis === 'measured' ? 'MEASURED' : 'MODELED') + ' · ' + n.denialRate.pct + '% denial rate (' + n.denialRate.source + ') · ' + fmtMoneyCompact(n.recoverable.value == null ? null : (n.recoverable.value / 100) * (n.denialRate.pct / 100)) +
        ' denials · root cause and AR 90+ pending the RCM rollup')
      : '';

  const setCaseMix = (ctx as PerfCtx & { setCaseMix?: (v: boolean) => void }).setCaseMix;
  const csv = () =>
    exportCsv(
      ['Rank', 'Facility', 'CCN', 'Beds', 'Grade', 'Composite', 'Delta', 'To next', 'Upside', 'Leak/yr ($K)', ...COLS.map((c) => c.label)],
      shown.map((r, i) => {
        const nx = nextGrade(r.comp);
        return [i + 1, r.name, r.ccn, r.beds, r.grade, r.comp, r.delta ?? '', nx ? '+' + nx.pts + ' to ' + nx.grade : 'top band', r.upsideUsd == null ? '' : usdK(r.upsideUsd), r.leakYr ?? '', ...r.cells.map((c) => (c === null ? '' : c))];
      }),
      'aithora-benchmark-' + ctx.tier + '.csv',
    );

  // The board as a PDF file: the same rows the CSV writes, one line each, made in the browser.
  const pdf = () =>
    saveBlob(
      textPdf(
        'Portfolio benchmark',
        `${shown.length} ${shown.length === 1 ? 'facility' : 'facilities'} · ${ctx.caseMix ? 'case-mix adjusted' : 'unadjusted'} percentiles · ${new Date().toISOString().slice(0, 10)}`,
        shown.map((r, i) => {
          const nx = nextGrade(r.comp);
          return `${i + 1}. ${r.name}${r.ccn ? ' (CCN ' + r.ccn + ')' : ''} · grade ${r.grade ?? '-'} · composite ${r.comp ?? '-'}${r.delta != null ? ' (' + r.delta + ')' : ''} · ${nx ? '+' + nx.pts + ' to ' + nx.grade : 'top band'}${r.upsideUsd == null ? '' : ' · upside ' + usdK(r.upsideUsd)}`;
        }),
      ),
      'aithora-benchmark-' + ctx.tier + '.pdf',
    );

  return (
    <BenchFrame label="Portfolio">
      {board && (
        <>
          <PortfolioRollup facilities={board.facilities} />
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:0 12px;margin-top:6px;')} data-testid="portfolio-kpis">
            <div style={sx(KPI_CELL)}>
              <div style={sx(KPI_K)}>Median composite</div>
              <div data-countup="1" style={sx(KPI_V)}>
                <span>{String(med)}</span>
              </div>
              <div style={sx('font-size:16px;color:#54606C;min-width:0;')}>
                across <span>{String(shown.length)}</span> shown
              </div>
            </div>
            <div style={sx(KPI_CELL)}>
              <div style={sx(KPI_K)}>Best</div>
              <div data-countup="1" style={sx(KPI_V + 'color:#1F6B52;')}>
                <span>{first ? String(first.comp) : '-'}</span>
              </div>
              <div style={sx('font-size:16px;color:#54606C;min-width:0;overflow-wrap:break-word;')}>
                <span>{first ? first.name : '-'}</span>
              </div>
            </div>
            <div style={sx(KPI_CELL)}>
              <div style={sx(KPI_K)}>Needs attention</div>
              <div data-countup="1" style={sx(KPI_V + 'color:#5B6B7C;')}>
                <span>{last ? String(last.comp) : '-'}</span>
              </div>
              <div style={sx('font-size:16px;color:#54606C;min-width:0;overflow-wrap:break-word;')}>
                <span>{last ? last.name : '-'}</span>
              </div>
            </div>
            <div style={sx(KPI_CELL)}>
              <div style={sx(KPI_K)}>Grade mix</div>
              <div style={sx('display:flex;height:10px;border-radius:5px;overflow:hidden;margin-top:8px;background:rgba(34,39,46,.07);grid-column:1 / -1;')}>
                {gradeMix.map((g) => (
                  <i key={g.label} style={sx('height:100%;background:' + g.color + ';width:' + g.w + ';')} />
                ))}
              </div>
              <div style={sx('display:flex;gap:12px;margin-top:6px;grid-column:1 / -1;font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;')}>
                {gradeMix.map((g) => (
                  <span key={g.label} style={sx('display:inline-flex;align-items:center;gap:5px;')}>
                    <i style={sx('width:6px;height:6px;border-radius:50%;flex:0 0 6px;background:' + g.color + ';')} />
                    <span>{g.label}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div style={sx('border-top:1px solid rgba(34,39,46,.14);padding:18px 20px;margin-top:12px;')}>
            <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:10px;')}>
              <span style={sx('font-size:16px;font-weight:700;letter-spacing:-.01em;flex:0 1 auto;min-width:0;')}>Portfolio heatmap</span>
              <span style={sx('font-size:16px;color:#54606C;')}>
                cells are percentile against <span>{peerLabel}</span>, click a row for its peer bands
              </span>
              <span style={sx('flex:1 1 auto;min-width:12px;')} />
              <span style={sx('display:inline-flex;align-items:center;gap:8px;flex:0 1 auto;min-width:0;flex-wrap:wrap;row-gap:6px;')}>
                <span style={sx('font-size:14px;letter-spacing:.12em;text-transform:uppercase;color:#54606C;font-weight:600;')}>Sort</span>
                <span role="group" aria-label="Sort" style={sx('display:inline-flex;flex:0 1 auto;min-width:0;border:1px solid rgba(34,39,46,.16);border-radius:7px;overflow:hidden;background:#FCFAF4;')}>
                  {(
                    [
                      ['comp', 'Composite'],
                      ['leak', 'Leakage $'],
                      ['beds', 'Beds'],
                      ['name', 'Name'],
                    ] as const
                  ).map(([k, label]) => {
                    const on = u.sortBy === k;
                    return (
                      <a
                        key={k}
                        href="#"
                        aria-pressed={on}
                        onClick={(e) => {
                          pre(e);
                          setBenchUi({ sortBy: k });
                        }}
                        style={sx('display:inline-block;padding:5px 11px;font-size:14px;text-decoration:none;' + (on ? 'color:#fff;background:#3C454F;font-weight:700;' : 'color:#54606C;'))}
                      >
                        {label}
                      </a>
                    );
                  })}
                </span>
                <a
                  href="#"
                  aria-pressed={ctx.caseMix}
                  onClick={(e) => {
                    pre(e);
                    setCaseMix?.(!ctx.caseMix);
                  }}
                  style={sx(
                    'display:inline-flex;align-items:center;gap:8px;height:30px;padding:0 12px;border-radius:8px;font-size:16px;font-weight:600;text-decoration:none;box-sizing:border-box;white-space:nowrap;' +
                      (ctx.caseMix ? 'background:#2F5C97;color:#fff;border:1px solid #2F5C97;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.16);'),
                  )}
                >
                  Case-mix adjusted
                </a>
                <span style={sx('display:inline-flex;align-items:center;gap:7px;height:30px;padding:0 10px;border-top:1px solid rgba(34,39,46,.14);box-sizing:border-box;white-space:nowrap;')}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#54606C" strokeWidth="1.9" style={sx('flex:0 0 13px;')} aria-hidden="true">
                    <circle cx="11" cy="11" r="6.6" />
                    <path d="m16 16 4.4 4.4" />
                  </svg>
                  <input
                    type="text"
                    aria-label="Search facilities"
                    value={u.qBench}
                    onChange={(e) => setBenchUi({ qBench: e.target.value, openRow: null })}
                    placeholder="Facility or CCN"
                    style={sx('border:0;outline:0;background:transparent;font-family:inherit;font-size:14px;color:#22272E;width:126px;')}
                  />
                </span>
                <a
                  href="#"
                  onClick={(e) => {
                    pre(e);
                    csv();
                  }}
                  style={sx('display:inline-flex;align-items:center;gap:7px;height:30px;padding:0 12px;border-top:1px solid rgba(34,39,46,.14);color:#3C454F;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;')}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 4v11" />
                    <path d="m7.5 11 4.5 4.5 4.5-4.5" />
                    <path d="M5 19.5h14" />
                  </svg>
                  CSV
                </a>
                <a
                  href="#"
                  data-testid="portfolio-pdf"
                  title="Saves the board as a PDF"
                  onClick={(e) => {
                    pre(e);
                    pdf();
                  }}
                  style={sx('display:inline-flex;align-items:center;gap:7px;height:30px;padding:0 12px;border-top:1px solid rgba(34,39,46,.14);color:#3C454F;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;')}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="6" y="3" width="12" height="7" rx="1" />
                    <path d="M6 14h12v7H6z" />
                    <path d="M4 10h16v4H4z" />
                  </svg>
                  PDF
                </a>
              </span>
            </div>
            <div style={sx('font-size:14px;color:#54606C;margin-top:6px;')}>
              <span>{ctx.caseMix ? 'Percentiles are acuity-adjusted against peers of comparable case-mix index.' : 'Percentiles are unadjusted: a higher-acuity building will read worse than it performs.'}</span>
            </div>
            <div style={sx('margin-top:14px;overflow-x:auto;')}>
              <div style={sx('min-width:1560px;')} role="table" aria-label="Portfolio heatmap">
                <div style={sx(GRID + 'align-items:end;padding-bottom:9px;border-bottom:1px solid rgba(34,39,46,.12);')} role="row">
                  {['#', 'Facility', 'Gr', 'Comp', 'Δ', 'To next', 'Upside', 'Leak / yr'].map((h) => (
                    <span key={h} style={sx(HEAD)} role="columnheader">
                      {h}
                    </span>
                  ))}
                  {COLS.map((c, i) => {
                    const src = srcOf(board, c.key);
                    return (
                      <span key={c.key} style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;text-align:center;line-height:1.15;')} role="columnheader">
                        <span>{c.label + dagger(i)}</span>
                        <span style={sx('display:block;text-align:center;font-size:14px;font-weight:700;letter-spacing:.06em;margin-top:2px;color:' + (src === 'LIVE' ? '#1F6B52' : '#54606C'))}>
                          <span>{src}</span>
                        </span>
                      </span>
                    );
                  })}
                </div>
                {shown.map((r, i) => {
                  const open = u.openRow === r.id;
                  const nx = nextGrade(r.comp);
                  const g = r.grade in GRADE_STYLE ? r.grade : gradeOf(r.comp);
                  const d = r.delta;
                  const rr = res[r.id];
                  return (
                    <div
                      key={r.id}
                      data-testid="heat-row"
                      style={sx('animation:riseIn .26s cubic-bezier(.22,.61,.36,1);animation-delay:' + Math.min(300, i * 24) + 'ms;border-bottom:1px solid rgba(34,39,46,.07);' + (open ? 'background:rgba(79,138,214,.045);' : ''))}
                    >
                      <div
                        role="row"
                        tabIndex={0}
                        aria-expanded={open}
                        onClick={() => toggleRow(r)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleRow(r);
                          }
                        }}
                        title={'CCN ' + r.ccn + ' · ' + r.beds + ' beds'}
                        style={sx(GRID + 'align-items:center;padding:3px 0;cursor:pointer;')}
                      >
                        <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;')}>
                          <span>{String(i + 1)}</span>
                        </span>
                        <span style={sx('display:flex;align-items:center;gap:7px;flex-wrap:nowrap;min-width:0;')}>
                          <span style={sx('color:#54606C;font-size:14px;flex:0 0 auto;')}>
                            <span>{open ? '▾' : '▸'}</span>
                          </span>
                          <span style={sx('display:flex;align-items:center;gap:4px 6px;flex-wrap:wrap;min-width:0;flex:1 1 0;')}>
                            <span style={sx('font-size:16px;font-weight:600;overflow-wrap:break-word;min-width:0;')}>
                              <span>{r.name}</span>
                            </span>
                            <span
                              style={sx(
                                r.capped
                                  ? 'display:inline-block;font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.08em;color:#B3261E;background:rgba(179,38,30,.16);border-radius:4px;padding:1px 5px;flex:0 0 auto;'
                                  : 'display:none;',
                              )}
                            >
                              <span>{r.capped ? 'CAPPED' : ''}</span>
                            </span>
                            <a
                              href="#"
                              data-testid="portfolio-compare-pick"
                              aria-pressed={cmp.includes(r.id)}
                              onClick={(e) => {
                                pre(e);
                                e.stopPropagation();
                                toggleCmp(r.id);
                              }}
                              onKeyDown={(e) => e.stopPropagation()}
                              style={sx(compareChip(cmp.includes(r.id)))}
                            >
                              {cmp.includes(r.id) ? 'Comparing' : 'Compare'}
                            </a>
                          </span>
                        </span>
                        <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:6px;font-size:16px;font-weight:800;color:' + GRADE_STYLE[g][0] + ';background:' + GRADE_STYLE[g][1] + ';white-space:nowrap;')}>
                          <span>{g}</span>
                        </span>
                        <span data-countup="1" style={sx('font-size:16px;font-weight:800;letter-spacing:-.02em;')}>
                          <span>{String(r.comp)}</span>
                        </span>
                        <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:600;color:' + (d === null || d === 0 ? '#54606C' : d > 0 ? '#1F6B52' : '#B3261E') + ';')}>
                          <span>{d === null || d === 0 ? '–' : d > 0 ? '▲' + d : '▼' + Math.abs(d)}</span>
                        </span>
                        <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:' + (nx && nx.pts <= 3 ? '#1F6B52' : '#54606C') + ';')}>
                          <span>{nx ? '+' + nx.pts + ' → ' + nx.grade : 'top band'}</span>
                        </span>
                        <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;')}>
                          <span>{usdK(r.upsideUsd)}</span>
                        </span>
                        <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:600;color:' + (r.leakYr === null ? '#54606C' : r.leakYr >= 40 ? '#B3261E' : r.leakYr >= 20 ? '#5B6B7C' : '#54606C') + ';')}>
                          <span>{r.leakYr === null ? '–' : '$' + r.leakYr + 'K'}</span>
                        </span>
                        {r.cells.map((c, ci) => {
                          const h = heatA(c);
                          return (
                            <span key={ci} role="cell" style={sx('display:flex;align-items:center;justify-content:center;height:24px;border-radius:5px;font-variant-numeric:tabular-nums;font-size:14px;font-weight:600;color:' + h.fg + ';background:' + h.bg + ';')}>
                              <span>{h.v}</span>
                            </span>
                          );
                        })}
                      </div>
                      {open && (
                        <div style={sx('padding:14px 0 18px 46px;border-top:1px dashed rgba(34,39,46,.16);')} data-testid="heat-detail">
                          <div style={sx('display:grid;grid-template-columns:180px minmax(0,1fr) 64px;gap:14px;align-items:center;padding-bottom:8px;')}>
                            <span style={sx('font-size:16px;font-weight:700;')}>
                              <span>{r.name}</span> on the peer band
                            </span>
                            <span style={sx('position:relative;display:block;height:13px;')}>
                              {[10, 50, 90].map((p) => (
                                <span key={p} style={sx('position:absolute;left:' + p + '%;top:0;transform:translateX(-50%);font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;')}>
                                  {'p' + p}
                                </span>
                              ))}
                            </span>
                            <span />
                          </div>
                          <div style={sx(r.capped ? 'margin-top:12px;padding:10px 12px;border-left:3px solid #B3261E;background:rgba(179,38,30,.06);font-size:16px;color:#54606C;line-height:1.45;' : 'display:none;')}>
                            <span>{r.capped ? 'Health inspection is 1-star : CMS caps the overall rating at one star above the inspection rating, so staffing and quality gains cannot lift this facility until survey performance moves.' : ''}</span>
                          </div>
                          <div style={sx('margin-bottom:14px;')}>
                            <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;')}>
                              <span style={sx('font-size:16px;font-weight:700;')}>Residents needing action</span>
                              <span style={sx('font-size:14px;color:#54606C;')}>Masked · minimum necessary: resident code, initials and room only. No PHI names on the cross-facility view.</span>
                            </div>
                            <div style={sx('display:grid;grid-template-columns:120px 66px 58px 78px 62px;gap:12px;padding:8px 0 5px;font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:600;')}>
                              <span>Code</span>
                              <span>Initials</span>
                              <span>Room</span>
                              <span>Mood</span>
                              <span>Billing</span>
                            </div>
                            {partner && <div style={sx('font-size:14px;color:#54606C;padding:5px 0;')} data-testid="residents-withheld">Shown to Aithora staff and the building's own admin.</div>}
                            {!partner && rr === undefined && <div style={sx('font-size:14px;color:#54606C;padding:5px 0;')}>Loading{'…'}</div>}
                            {rr === 'none' && <div style={sx('font-size:14px;color:#54606C;padding:5px 0;')} data-testid="empty-state">{plain('Not served yet: the masked list of residents needing action.')}</div>}
                            {typeof rr === 'object' &&
                              rr.residents.map((p) => (
                                <div key={p.code} style={sx('display:grid;grid-template-columns:120px 66px 58px 78px 62px;gap:12px;align-items:center;padding:5px 0;border-top:1px solid rgba(34,39,46,.07);font-size:14px;')}>
                                  <span style={sx('font-variant-numeric:tabular-nums;color:#54606C;')}>{p.code}</span>
                                  <span style={sx('font-weight:600;')}>{p.initials}</span>
                                  <span style={sx('font-variant-numeric:tabular-nums;color:#54606C;')}>{p.room}</span>
                                  <span style={sx('color:#54606C;')}>{p.mood}</span>
                                  <span style={sx('font-weight:600;color:' + (p.billing === 'ready' ? '#1F6B52' : '#5B6B7C') + ';')}>{p.billing === 'ready' ? 'Ready' : 'Blocked'}</span>
                                </div>
                              ))}
                          </div>
                          {r.cells.map((c, ci) => {
                            const color = c === null ? '#54606C' : pc(c);
                            return (
                              <div key={ci} style={sx('display:grid;grid-template-columns:180px minmax(0,1fr) 64px;gap:14px;align-items:center;padding:1px 0;')}>
                                <span style={sx('font-size:16px;color:#54606C;')}>{COLS[ci].label + dagger(ci)}</span>
                                <span style={sx('position:relative;display:block;height:7px;border-radius:4px;background:rgba(34,39,46,.05);')}>
                                  <i style={sx('position:absolute;top:0;bottom:0;left:10%;width:80%;border-radius:4px;background:rgba(47,92,151,.1);')} />
                                  <i style={sx('position:absolute;top:-2px;bottom:-2px;left:50%;width:1px;background:rgba(34,39,46,.22);')} />
                                  <i style={sx('position:absolute;top:50%;width:8px;height:8px;margin-top:-4px;margin-left:-4px;border-radius:2px;transform:translateX(-50%) rotate(45deg);background:' + color + ';left:' + (c === null ? 50 : c) + '%;')} />
                                </span>
                                <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:' + color + ';text-align:right;')}>{c === null ? '-' : ord(c)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {!shown.length && <div style={sx('font-size:16px;color:#54606C;padding:14px 0;')}>No facility matches this filter.</div>}
                {unscored.map((e) => (
                  <div key={e.id} data-testid="heat-unscored" style={sx(GRID + 'align-items:center;padding:9px 0;opacity:.65;')}>
                    <span />
                    <span style={sx('display:flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:6px;min-width:0;')}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#54606C" strokeWidth="1.8" style={sx('flex:0 0 13px;')} aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 11v5.5M12 7.6v.6" />
                      </svg>
                      <span style={sx('min-width:0;')}>
                        <span style={sx('display:block;font-size:16px;font-weight:600;color:#54606C;')}>{e.name}</span>
                        <span style={sx('display:block;font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;')}>{reasonText(e.reason)}</span>
                      </span>
                    </span>
                    <span style={sx('font-size:16px;color:#54606C;')}>-</span>
                    <span style={sx('font-size:16px;color:#54606C;')}>-</span>
                    <span />
                    <span />
                    <span />
                    <span />
                    {COLS.map((c) => (
                      <span key={c.key} style={sx('display:flex;align-items:center;justify-content:center;height:24px;border-radius:5px;background:repeating-linear-gradient(45deg,rgba(34,39,46,.04),rgba(34,39,46,.04) 3px,transparent 3px,transparent 6px);')} />
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div style={sx('display:flex;align-items:center;gap:16px;flex-wrap:wrap;row-gap:6px;margin-top:14px;padding-top:12px;border-top:1px solid rgba(34,39,46,.09);font-size:14px;font-variant-numeric:tabular-nums;color:#54606C;')}>
              <span>Percentile</span>
              {(
                [
                  ['#F1F4F8', '<40', true],
                  ['#D3DEEC', '40–59', false],
                  ['#9DB4D3', '60–74', false],
                  ['#2F5C97', '75–89', false],
                  ['#1E3F6E', '90+', false],
                ] as const
              ).map(([bg, label, ring]) => (
                <span key={label} style={sx('display:inline-flex;align-items:center;gap:5px;')}>
                  <i style={sx('width:16px;height:9px;border-radius:2px;background:' + bg + ';' + (ring ? 'box-shadow:inset 0 0 0 1px rgba(34,39,46,.14);' : ''))} />
                  {label}
                </span>
              ))}
              <span style={sx('flex:1;')} />
              <span>
                <span>{unscored.length ? unscored.length + (unscored.length === 1 ? ' facility' : ' facilities') + ' not scoreable, no CMS match' : ''}</span>
              </span>
            </div>
            <div style={sx('margin-top:8px;font-size:14px;font-variant-numeric:tabular-nums;color:#54606C;line-height:1.5;')}>
              {'†'} Health inspection, survey, penalties, fines and both deficiency measures are ranked against state peers per CMS methodology and do not follow the peer-tier selector. Upside is annual revenue at a 4% private-pay census lift per grade step.
              Composite is a percentile rank against facilities of the same type, state and size. The Case-mix adjusted switch shifts the composite for resident acuity; individual staffing and quality rows stay as reported.
            </div>
            {(() => {
              const pick = cmp.map((id) => shown.find((r) => r.id === id)).filter((r): r is BenchRow => !!r);
              return pick.length === 2 ? (
                <SideBySide a={pick[0]} b={pick[1]} entry={entryOf} />
              ) : (
                <div data-testid="portfolio-compare-hint" style={sx('font-size:16px;color:#54606C;margin-top:10px;')}>
                  Pick two rows with Compare to see them side by side.
                </div>
              );
            })()}
          </div>

          <div data-screen-label="Performance · Portfolio strip" style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,230px),1fr));gap:12px 0;margin:10px -16px 0;padding-top:10px;border-top:1px solid rgba(34,39,46,.14);')}>
            <div style={sx(STRIP_COL + 'border-left:none;')}>
              <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;min-width:0;')}>
                <span style={sx(SECTION)}>Priorities to improve</span>
                <span style={sx('font-size:14px;color:#54606C;')}>
                  <span>{prio.length ? prio.length + ' of ' + COLS.length + ' metrics sit below what is typical for peers' : 'nothing below what is typical for peers'}</span>
                </span>
              </div>
              {prio.map((p) => (
                <div key={p.label} title={p.gain} style={sx('display:grid;grid-template-columns:minmax(0,max-content) auto minmax(36px,1fr);gap:8px;align-items:center;min-width:0;')}>
                  <span style={sx('font-size:16px;font-weight:600;color:#22272E;overflow-wrap:break-word;min-width:0;')}>
                    <span>{p.label}</span>
                  </span>
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;color:' + p.color + ';white-space:nowrap;')}>
                    <span>{p.pct}</span>
                  </span>
                  <span style={sx('height:5px;border-radius:3px;background:rgba(34,39,46,.07);overflow:hidden;min-width:0;')}>
                    <i style={sx('display:block;height:100%;border-radius:3px;background:' + p.color + ';width:' + p.w + ';')} />
                  </span>
                </div>
              ))}
              <div title={prioNote} style={sx('font-size:14px;color:#54606C;line-height:1.4;text-wrap:pretty;')}>
                <span>{prioNote}</span>
              </div>
            </div>
            <div style={sx(STRIP_COL)}>
              <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;min-width:0;')}>
                <span style={sx(SECTION)}>Where the portfolio moves</span>
              </div>
              {pmCols.map((c) => (
                <div key={c.head} style={sx('display:flex;flex-direction:column;gap:1px;min-width:0;')}>
                  <div style={sx('display:flex;align-items:baseline;gap:7px;flex-wrap:wrap;min-width:0;')}>
                    <span style={sx('font-size:14px;font-weight:700;color:#3C454F;')}>
                      <span>{c.head}</span>
                    </span>
                    <BuildTagged tag={c.tag}>
                    <span style={sx(c.tag ? 'font-size:14px;font-weight:800;letter-spacing:.08em;color:#2F5C97;background:rgba(47,92,151,.1);border-radius:3px;padding:1px 4px;' : 'display:none;')}>
                      <span>{c.tag}</span>
                    </span>
                    </BuildTagged>
                  </div>
                  {c.rows.map((m) => (
                    <a
                      key={m.key}
                      href="#"
                      title={m.tip}
                      onClick={(e) => {
                        pre(e);
                        setBenchUi({ openRow: m.id });
                      }}
                      style={sx('display:flex;align-items:baseline;gap:8px;padding:2px 6px;margin:0 -6px;border-radius:6px;text-decoration:none;color:#22272E;min-width:0;animation:riseIn .3s cubic-bezier(.22,.61,.36,1);')}
                    >
                      <span style={sx('flex:0 1 auto;min-width:0;font-size:16px;font-weight:600;overflow-wrap:break-word;')}>
                        <span>{m.a}</span>
                      </span>
                      <span style={sx('flex:0 0 auto;font-size:14px;color:#54606C;white-space:nowrap;')}>
                        <span>{m.b}</span>
                      </span>
                      <span style={sx(V(m.c))}>
                        <span>{m.v}</span>
                      </span>
                    </a>
                  ))}
                  <span style={sx(c.rows.length ? 'display:none;' : 'font-size:14px;color:#54606C;padding:4px 0;')}>
                    <span>{c.rows.length ? '' : c.empty}</span>
                  </span>
                </div>
              ))}
            </div>
            <div style={sx(STRIP_COL)}>
              <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;min-width:0;')}>
                <span style={sx(SECTION)}>Outliers worth a look</span>
              </div>
              {outliers.map((o) => (
                <div key={o.tag} title={o.why} style={sx('min-width:0;')}>
                  <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;min-width:0;')}>
                    <span style={sx('font-size:16px;font-weight:700;color:#22272E;min-width:0;')}>
                      <span>{o.name}</span>
                    </span>
                    <BuildTagged tag={o.tag}><span style={sx(o.tagStyle)}>
                      <span>{o.tag}</span>
                    </span></BuildTagged>
                  </div>
                  <div style={sx('font-size:16px;color:#3C454F;line-height:1.4;text-wrap:pretty;')}>
                    <span>{o.why}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={sx(STRIP_COL)}>
              <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;min-width:0;')}>
                <span style={sx(SECTION)}>Market context</span>
              </div>
              {market.map((m) => (
                <div key={m.k} style={sx('display:flex;align-items:baseline;gap:8px;min-width:0;')}>
                  <span style={sx('font-size:14px;color:#54606C;flex:0 1 auto;min-width:0;overflow-wrap:break-word;')}>
                    <span>{m.k}</span>
                  </span>
                  <span style={sx('flex:1 1 12px;border-bottom:1px dotted rgba(34,39,46,.2);min-width:12px;')} />
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;color:#22272E;flex:0 0 auto;')}>
                    <span>{m.v}</span>
                  </span>
                </div>
              ))}
              <div title={marketNote} style={sx('font-size:14px;color:#54606C;line-height:1.4;text-wrap:pretty;')}>
                <span>{marketNote}</span>
              </div>
            </div>
          </div>

          <div style={sx('border-top:1px solid rgba(34,39,46,.1);margin-top:10px;padding-top:8px;font-size:14px;color:#54606C;line-height:1.6;max-width:140ch;')}>
            Honesty notes. Peer groups smaller than 11 facilities are marked with an asterisk and their percentiles are wide: read them as a direction, not a rank. A cell reads as unscoreable rather than zero when CMS has published no value for it. Nothing on this page is case-mix adjusted unless the Case-mix
            adjusted control is on, so a facility with a heavier population will look worse on staffing and quality than its work deserves. LIVE columns come from Aithora care tables and are current to yesterday; CMS columns are current to the last published quarter, so the two describe different
            moments.
          </div>

          <div style={sx('display:flex;flex-wrap:wrap;gap:8px 28px;margin-top:8px;padding-top:8px;border-top:1px solid rgba(34,39,46,.1);align-items:center;')}>
            <div style={sx('display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;min-width:0;flex:1 1 360px;')}>
              <span style={sx('font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;flex:0 0 auto;')}>Top of portfolio</span>
              {shown.slice(0, 3).map((r) => (
                <span key={r.id} style={sx('display:inline-flex;align-items:baseline;gap:6px;font-size:16px;font-weight:600;color:#22272E;min-width:0;white-space:nowrap;')}>
                  <span>{r.name}</span>
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;color:#1F6B52;')}>
                    <span>{r.comp + ' · ' + gradeOf(r.comp)}</span>
                  </span>
                </span>
              ))}
            </div>
            <div style={sx('display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;min-width:0;flex:1 1 360px;')}>
              <span style={sx('font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;flex:0 0 auto;')}>Needs attention</span>
              {shown
                .slice(-3)
                .reverse()
                .map((r) => (
                  <span key={r.id} style={sx('display:inline-flex;align-items:baseline;gap:6px;font-size:16px;font-weight:600;color:#22272E;min-width:0;white-space:nowrap;')}>
                    <span>{r.name}</span>
                    <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;color:#B3261E;')}>
                      <span>{r.comp + ' · ' + gradeOf(r.comp)}</span>
                    </span>
                  </span>
                ))}
            </div>
            <span style={sx('font-size:14px;color:#54606C;flex:0 1 auto;')}>
              <span>{agingLine}</span>
            </span>
          </div>
        </>
      )}
    </BenchFrame>
  );
}
